import { useState, useEffect, useMemo } from 'react';
import type { CartItemStatus, CartStatus, ItemStatusCode } from '@/features/cart/types';
import { ItemStatusCode as ItemStatusCodeConst, CartStatus as CartStatusConst } from '@/features/cart/types';
import { useCartItemStatusTest } from '@/features/cart/components/CartItemStatusSwitcher';

export interface UseCartItemStatusReturn {
  status: CartItemStatus | null;
  statusLoading: boolean;
  statusCode: ItemStatusCode | undefined;
  addOnStatuses: Array<{ add_on_id: string; status_code: ItemStatusCode }>;
  borderColor: string;
  canEdit: boolean;
}

export function useCartItemStatus(
  itemStatus: CartItemStatus | null | undefined,
  cartStatus: CartStatus | undefined,
  cartItemId?: string
): UseCartItemStatusReturn {
  const [status, setStatus] = useState<CartItemStatus | null>(itemStatus || null);
  const [statusLoading, setStatusLoading] = useState(itemStatus === undefined);
  const { globalTestItemStatus, getTestItemStatus, globalTestAddOnStatus } = useCartItemStatusTest();

  // Sync status when parent provides it
  useEffect(() => {
    if (itemStatus !== undefined) {
      setStatus(itemStatus);
      setStatusLoading(false);
    } else {
      // Only show loading if cart status is ACTIVE (status endpoints are being fetched)
      setStatusLoading(cartStatus === CartStatusConst.Active);
    }
  }, [itemStatus, cartStatus]);

  // Get test override for this specific item or global override
  const testItemStatus = cartItemId ? getTestItemStatus(cartItemId) : null;
  const effectiveTestItemStatus = testItemStatus || globalTestItemStatus;

  // Use test status if set, otherwise use real status
  const statusCode = effectiveTestItemStatus || status?.item_status_code;

  // Apply test add-on status to all add-ons if set
  const addOnStatuses = useMemo(() => {
    const realStatuses = status?.add_on_statuses || [];
    if (globalTestAddOnStatus) {
      // Override all add-on statuses with the test status
      return realStatuses.map(s => ({
        ...s,
        status_code: globalTestAddOnStatus,
      }));
    }
    return realStatuses;
  }, [status?.add_on_statuses, globalTestAddOnStatus]);

  // Check if cart allows editing (only ACTIVE status allows editing)
  const canEdit = cartStatus === CartStatusConst.Active;

  // Get border color based on status and cart status
  const borderColor = useMemo((): string => {
    if (statusLoading) return 'border-gray-500/50';

    // If cart is REFUNDED or ABANDONED, show grey border
    if (cartStatus === CartStatusConst.Refunded || cartStatus === CartStatusConst.Abandoned) {
      return 'border-gray-500/50';
    }

    // If cart is not ACTIVE, show green border (items are locked)
    if (cartStatus && cartStatus !== CartStatusConst.Active) {
      return 'border-green-500/50';
    }

    // For ACTIVE carts, use item status to determine border color
    if (statusCode === ItemStatusCodeConst.GoodForBooking) {
      return 'border-green-500/50';
    }
    if (statusCode === ItemStatusCodeConst.PriceUpdateRequired) {
      return 'border-yellow-500/50';
    }
    if (statusCode === ItemStatusCodeConst.GoodButAddOnIssues) {
      return 'border-yellow-500/50';
    }
    if (statusCode) {
      return 'border-red-500/50';
    }
    return 'border-gray-700/50';
  }, [statusCode, cartStatus, statusLoading]);

  return {
    status,
    statusLoading,
    statusCode,
    addOnStatuses,
    borderColor,
    canEdit,
  };
}

